import { useState, useEffect } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useFormik } from 'formik'
import { deleteBusiness } from '../../../api'
import { useAuth } from '../core/Auth'

const initialValues = {
  deleteCode: '',
  feedback: '',
  confirmText: ''
}

const deleteConfirmationSchema = Yup.object().shape({
  deleteCode: Yup.string()
    .min(32, 'Delete code must be 32 characters')
    .max(32, 'Delete code must be 32 characters')
    .required('Delete confirmation code is required'),
  confirmText: Yup.string()
    .oneOf(['DELETE MY ACCOUNT'], 'You must type "DELETE MY ACCOUNT" to confirm')
    .required('Confirmation text is required'),
  feedback: Yup.string().max(500, 'Feedback must be less than 500 characters')
})

export function DeleteConfirmation() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [searchParams] = useSearchParams()
  const deleteCodeFromUrl = searchParams.get('deleteCode')
  const [loading, setLoading] = useState(false)
  const [formSuccess, setFormSuccess] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState('')

  const formik = useFormik({
    initialValues,
    validationSchema: deleteConfirmationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      setHasErrors(undefined)

      try {
        await deleteBusiness({
          deleteCode: values.deleteCode,
          feedback: values.feedback || undefined
        })
        setFormSuccess(true)
        logout()
        setTimeout(() => {
          navigate('/auth/login')
        }, 3000)
      } catch (error: any) {
        setHasErrors(true)
        const errorMsg = error.response?.data?.message || 'Failed to delete account'.toTranslatedDisplay()
        setErrorMessage(errorMsg)
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (deleteCodeFromUrl) {
      formik.setFieldValue('deleteCode', deleteCodeFromUrl)
    }
  }, [deleteCodeFromUrl])

  const title = 'Confirm Account Deletion'.toTranslatedDisplay()

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_delete_confirmation_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <h1 className='text-gray-900 fw-bolder mb-3'>{title}</h1>
        
        {formSuccess ? (
          <>
            <div className='mb-10 bg-light-success p-8 rounded'>
              <div className='text-success fw-bold'>
                <i className='bi bi-check-circle fs-2x text-success mb-3 d-block'></i>
                <div>{'Your account has been permanently deleted.'.toTranslatedDisplay()}</div>
                <div className='mt-2 fs-7'>{'Redirecting to login page...'.toTranslatedDisplay()}</div>
              </div>
            </div>
            <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
              <button onClick={() => { navigate('/auth/login') }} className='btn btn-primary'>
                {'Login'.toTranslatedDisplay()}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Final Warning Alert */}
            <div className='mb-10 bg-light-danger p-8 rounded'>
              <div className='text-danger fw-bold'>
                <i className='bi bi-exclamation-triangle fs-2x text-danger mb-3 d-block'></i>
                <div className='fs-4 mb-2'>{'FINAL WARNING'.toTranslatedDisplay()}</div>
                <div className='mb-3'>{'This will permanently delete your account and all associated data.'.toTranslatedDisplay()}</div>
                <div className='text-start'>
                  <ul className='mb-0 text-danger'>
                    <li>{'All your business data will be permanently deleted'.toTranslatedDisplay()}</li>
                    <li>{'All transactions, reports, and settings will be lost'.toTranslatedDisplay()}</li>
                    <li>{'Your account cannot be recovered after deletion'.toTranslatedDisplay()}</li>
                    <li>{'Active subscriptions will be cancelled'.toTranslatedDisplay()}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className='text-gray-500 fw-semibold fs-6 mb-8'>{'Please enter your delete confirmation code and type the required text to proceed'.toTranslatedDisplay()}</div>

            {/* Delete Code Field */}
            <div className='fv-row mb-8'>
              <label className='form-label fw-bolder text-gray-900 fs-6'>{'Delete Confirmation Code'.toTranslatedDisplay()}</label>
              <input
                type='text'
                placeholder={'Enter the 32-character code from your email'.toTranslatedDisplay()}
                autoComplete='off'
                {...formik.getFieldProps('deleteCode')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.deleteCode && formik.errors.deleteCode,
                  },
                  {
                    'is-valid': formik.touched.deleteCode && !formik.errors.deleteCode,
                  }
                )}
              />
              {formik.touched.deleteCode && formik.errors.deleteCode && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.deleteCode?.toTranslatedDisplay()}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Confirmation Text Field */}
            <div className='fv-row mb-8'>
              <label className='form-label fw-bolder text-gray-900 fs-6'>{'Type "DELETE MY ACCOUNT" to confirm'.toTranslatedDisplay()}</label>
              <input
                type='text'
                placeholder={'DELETE MY ACCOUNT'.toTranslatedDisplay()}
                autoComplete='off'
                {...formik.getFieldProps('confirmText')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.confirmText && formik.errors.confirmText,
                  },
                  {
                    'is-valid': formik.touched.confirmText && !formik.errors.confirmText,
                  }
                )}
              />
              {formik.touched.confirmText && formik.errors.confirmText && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.confirmText?.toTranslatedDisplay()}</span>
                  </div>
                </div>
              )}
              <div className='text-muted mt-1'>{'This helps ensure you understand the consequences of this action.'.toTranslatedDisplay()}</div>
            </div>

            {/* Optional Feedback Field */}
            <div className='fv-row mb-8'>
              <label className='form-label fw-bolder text-gray-900 fs-6'>{'Feedback (Optional)'.toTranslatedDisplay()}</label>
              <textarea
                placeholder={'Tell us why you are deleting your account (this helps us improve our service)'.toTranslatedDisplay()}
                rows={2}
                {...formik.getFieldProps('feedback')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.feedback && formik.errors.feedback,
                  },
                  {
                    'is-valid': formik.touched.feedback && !formik.errors.feedback,
                  }
                )}
              />
              {formik.touched.feedback && formik.errors.feedback && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.feedback?.toTranslatedDisplay()}</span>
                  </div>
                </div>
              )}
              <div className='text-muted fs-7 mt-1'>
                {formik.values.feedback.length}/500 {'characters'.toTranslatedDisplay()}
              </div>
            </div>
              {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            {errorMessage || 'Sorry, looks like there are some errors detected, please try again.'.toTranslatedDisplay()}
          </div>
        </div>
      )}
            <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
              <button 
                type='submit' 
                className='btn btn-danger me-4'
                disabled={loading || !formik.isValid}
              >
                {loading ? (
                  <span className='indicator-progress'>
                    {'Deleting Account...'.toTranslatedDisplay()}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                ) : (
                  <span className='indicator-label'>
                    <i className='bi bi-trash me-2'></i>
                    {'Delete My Account Forever'.toTranslatedDisplay()}
                  </span>
                )}
              </button>
              <Link to='/dashboard'>
                <button
                  type='button'
                  className='btn btn-light'
                  disabled={loading}
                >{'Cancel'.toTranslatedDisplay()}</button>
              </Link>
            </div>
          </>
        )}
      </div>

      
    </form>
  )
}